import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import { StatusBadge } from "components/common/StatusBadge";
import Switch from "components/common/Switch";
import TableShimmer from "components/common/TableShimmer";
import { handleStatusUpdate } from "helpers/apiUtils";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Modal, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  getPlans,
  getPlansList,
  resetPlanFilters,
  setPlanFilter,
  updatePlanStatus,
} from "store/slices/membershipSlice";
import { showDeleteModal } from "store/slices/modalSlice";
import {
  ORDER_BY_OPTIONS,
  PLAN_OPTIONS,
  STATUS_OPTIONS,
} from "utils/constants";
import { getActionFlags } from "utils/getActionFlags";
import { formatUTCDate, getMinMaxDate } from "utils/utils";
import Paginations from "components/common/Paginations";
import { usePermission } from "hooks/usePermission";
import PageHeader from "components/common/PageHeader";
import { getDynamicPrices } from "store/slices/dynamicPricesSlice";
import { formatOptionsForSelect } from "utils/formatOptions";

const DynamicPricesList = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { trash, edit, noPlans, view } = Icons || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { canAction } = usePermission();
  const { canView, canEdit, canDelete, canAdd } = canAction("membership_plan");

  // Split selectors to prevent unnecessary re-renders
  const {
    loading,
    isLoading,
    items: dynamicPrices,
    loadingItemId,
    totalPages,
    currentPage,
    totalCount,
    filters: { perPage, pageNo, status, orderBy, keyword, dates, planId, type },
  } = useSelector((state) => state.DynamicPrices);
  const { plansList } = useSelector((state) => state.Membership);

  useEffect(() => {
    if (canView) {
      dispatch(getPlansList());
      getAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const getAllData = useCallback(
    (page, action) => {
      const { isReset, isResetKeyword } = getActionFlags(action);

      const payload = {
        perPage,
        pageNo: page || pageNo,
        status: isReset ? -1 : status,
        orderBy: isReset ? -1 : orderBy,
        planId: isReset ? "" : planId,
        keyword: isResetKeyword ? "" : keyword || "",
        dateRange: isReset ? {} : getMinMaxDate(dates),
      };

      dispatch(getDynamicPrices(payload));
    },
    [perPage, pageNo, status, orderBy, keyword, dates, dispatch],
  );

  const handleReset = useCallback(() => {
    dispatch(resetPlanFilters());
    getAllData(1, { actionType: "RESET" });
  }, [dispatch, getAllData]);

  const filtersList = useMemo(
    () => [
      {
        key: "status",
        placeholder: t("Status"),
        type: "singleSelect",
        options: STATUS_OPTIONS,
      },
      {
        key: "planId",
        placeholder: t("Select Plan"),
        type: "singleSelect",
        options: formatOptionsForSelect(plansList, language, "title"),
      },
      {
        key: "type",
        placeholder: t("Type"),
        type: "singleSelect",
        options: PLAN_OPTIONS,
      },
      {
        key: "orderBy",
        placeholder: t("Order By"),
        type: "singleSelect",
        options: ORDER_BY_OPTIONS,
      },
    ],
    [t],
  );

  if (!canView) return null;

  return (
    <div className="page-content addons_page">
      <div className="g_between">
        <PageHeader title={t("Dynamic Prices Rules")} />
      </div>

      <FilterBar
        keyword={keyword}
        setKeyword={(value) =>
          dispatch(setPlanFilter({ key: "keyword", value }))
        }
        keywordPlaceholder={t("Search by Title")}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy, planId, type }}
        onFilterUpdate={(key, value) => dispatch(setPlanFilter({ key, value }))}
        dates={dates}
        setDates={(value) => dispatch(setPlanFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
      />

      {loading && <TableShimmer rows={12} columns={8} />}

      {!loading && dynamicPrices?.length === 0 && (
        <NoDataFound title={t("No dynamic prices found")} icon={noPlans} />
      )}

      {!loading && dynamicPrices?.length > 0 && (
        <div className="common_table_style">
          <Table hover responsive>
            <thead className="t_head">
              <tr>
                <th style={{ width: 70 }}>{t("S.No")}</th>
                <th>{t("Plan Type")}</th>
                <th>{t("Price Type")}</th>
                <th>{t("Value")}</th>
                <th>{t("Maximum Customers")}</th>
              </tr>
            </thead>
            <tbody>
              {dynamicPrices?.length > 0 ? (
                dynamicPrices.map((addon, index) => {
                  const {
                    _id,
                    planId,
                    type,
                    maxCustomers,
                    value,
                    status,
                    createdAt,
                  } = addon || {};
                  return (
                    <tr key={_id}>
                      <td>{index + 1}</td>

                      <td>
                        <div className="">
                          <Link
                            to={`/membership/plans/plan-details?id=${planId?._id}`}
                          >
                            {planId?.planType}
                          </Link>
                        </div>
                      </td>
                      <td>
                        <div className="ltr_text">{type}</div>
                      </td>
                      <td>
                        <div className="ltr_text">{value}</div>
                      </td>
                      <td>
                        <div className="ltr_text">{maxCustomers}</div>
                      </td>
                      <td>
                        <div className="ltr_text">
                          {formatUTCDate(createdAt)}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-3 text-muted">
                    {t("No Dynamic Prices found")}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      {!loading && (
        <Paginations
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={getAllData}
        />
      )}
    </div>
  );
};

export default DynamicPricesList;
